"use client";
import Image from "next/image";
import { useEffect, useState } from "react";

type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

const KEY = "paldeck:install-dismissed";
const SNOOZE_DAYS = 14;
const DELAY_MS = 20_000;

const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage blocked */
  }
};

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
function isPhone() {
  return window.matchMedia("(pointer: coarse)").matches && window.matchMedia("(max-width: 900px)").matches;
}
/** iPhone/iPad Safari (other iOS browsers and in-app browsers can't add to the home screen the same way) */
function isIosSafari() {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|Instagram|FBAN|FBAV|Line\//.test(ua);
}

/** Registers the service worker and offers "add to home screen" on phones. */
export default function InstallPrompt() {
  const [event, setEvent] = useState<BIPEvent | null>(null);
  const [mode, setMode] = useState<"android" | "ios" | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => undefined);
    }
    if (isStandalone() || !isPhone()) return;
    const dismissed = Number(read(KEY) ?? 0);
    if (dismissed && Date.now() - dismissed < SNOOZE_DAYS * 86_400_000) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvent(e as BIPEvent);
      setMode("android");
    };
    const onInstalled = () => setShow(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (isIosSafari()) setMode("ios");
    const t = window.setTimeout(() => setShow(true), DELAY_MS);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!show || !mode) return null;
  const close = () => {
    write(KEY, String(Date.now()));
    setShow(false);
  };
  const install = async () => {
    if (!event) return;
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === "accepted") setShow(false);
    else close();
  };

  return (
    <div role="dialog" aria-label="Install PalDeck" className="fixed inset-x-3 bottom-3 z-50 rounded-2xl border border-deck-line bg-deck-card/95 p-4 shadow-2xl backdrop-blur" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
      <div className="flex items-start gap-3">
        <Image src="/icons/icon-192.png" width={44} height={44} alt="" className="shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="font-display font-semibold text-deck-text">Add PalDeck to your home screen</p>
          {mode === "android" ? (
            <p className="mt-0.5 text-sm text-deck-muted">Opens full screen like an app, so breeding combos are one tap away mid-game.</p>
          ) : (
            <p className="mt-0.5 text-sm text-deck-muted">
              Tap{" "}
              <svg aria-label="Share" viewBox="0 0 24 24" className="inline h-4 w-4 -translate-y-px fill-none stroke-deck-cyan stroke-2">
                <path d="M12 3v12M7 8l5-5 5 5M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
              </svg>{" "}
              <span className="text-deck-text">Share</span>, then <span className="text-deck-text">Add to Home Screen</span>.
            </p>
          )}
        </div>
        <button onClick={close} aria-label="Close" className="-mr-1 -mt-1 rounded-md px-2 py-1 text-lg leading-none text-deck-muted hover:text-deck-text">
          ×
        </button>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={close} className="rounded-lg px-3 py-2 text-sm text-deck-muted hover:text-deck-text">
          Not now
        </button>
        {mode === "android" && (
          <button onClick={install} className="rounded-lg bg-deck-cyan px-4 py-2 text-sm font-semibold text-deck-bg">
            Install
          </button>
        )}
      </div>
    </div>
  );
}
